import { LmfAnalyticsTrackEventActions } from '@syconium/little-miss-figgy'

import { SessionReplayPrivacyConfig } from '../../types/amplitude-session-replay'
import { type TeamsEventName } from '../_components/pages/organization/hooks/useTeamsTracking'
import { type StaticAmplitudeEventName } from '../_hooks/useStaticTracking'

import { ConsentPolicy } from './ConsentPolicy.config'
import { ExperimentationContext } from './Experimentation.config'
import { SessionAttributes } from './Session.config'

type AmplitudeEventName =
  | LmfAnalyticsTrackEventActions
  | TeamsEventName
  | StaticAmplitudeEventName
  | 'flag-decision'

export type AmplitudeEvent = {
  eventName: AmplitudeEventName
  eventId: string | undefined
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  eventProperties: Record<string, any>
}

export const amplitudeSessionReplayFlagKey = 'amplitude-session-replay'

export const sessionReplayPrivacyConfig: SessionReplayPrivacyConfig = {
  defaultMaskLevel: 'conservative',
  // Escape hatch, unused today. A component reviewed as safe to record opts in with
  // `data-amp-unmask`, which also unmasks its descendants.
  unmaskSelector: ['[data-amp-unmask]'],
}

export const getSessionReplaySampleRate = (): number => {
  const percentage = Number.parseFloat(
    process.env.NEXT_PUBLIC_AMPLITUDE_SESSION_REPLAY_SAMPLE_RATE ?? '0'
  )
  if (!Number.isFinite(percentage)) return 0
  return Math.min(Math.max(percentage, 0), 100) / 100
}

export const getAmplitudeFlagDecisionAnalytics = (flagKey: string, flagDecision: string) => {
  const userPropertyKey = 'active-flags'
  const event = {
    userProperties: {
      [userPropertyKey]: `${flagKey}:${flagDecision}`,
    },
    eventId: `${userPropertyKey}:${flagKey}:${flagDecision}`,
    eventName: 'flag-decision',
    eventProperties: {
      variation: flagDecision,
      key: flagKey,
    },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } as const satisfies AmplitudeEvent & { userProperties: Record<string, any> }
  return event
}

// Just reuse LaunchDarkly context, but flatten it to just string, string records.
export const convertExperimentationContextToAmplitudeIdentity = ({
  accountId,
  session,
  experimentationContext,
  consentPolicy,
}: {
  accountId: string | undefined
  session: SessionAttributes
  experimentationContext: ExperimentationContext
  consentPolicy: ConsentPolicy
}): Record<string, string> => {
  // Flatten defaults
  const amplitudeIdentity: Record<string, string> = {}
  const sourceContexts = {
    ...experimentationContext,
    consent: consentPolicy,
    source: session.source ?? {},
  }
  Object.keys(sourceContexts).forEach(subContextKey => {
    const subContext = sourceContexts[subContextKey as keyof typeof sourceContexts]
    if (
      typeof subContext === 'object' &&
      // TODO: Fix this auto-generated suppression.
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      !!subContext &&
      (subContextKey !== 'account' || !!accountId) // LD has placeholder account id fillers. Ignore account context if not logged in
    ) {
      Object.keys(subContext).forEach(fieldKey => {
        const fieldValue = subContext[fieldKey as keyof typeof subContext]
        if (typeof fieldValue === 'string') {
          amplitudeIdentity[`${subContextKey}-${fieldKey}`] = fieldValue
        }
      })
    }
  })

  // Enhance
  amplitudeIdentity['session-key'] = session.sessionId // Experimentation doesn't use sessions, so append the session id here.

  return amplitudeIdentity
}
